import React from 'react';
import { IReceiptData } from '../../../ApiCalls';
import DateTimeInput from './Forms/DateTimeInput';
import ProgressBar from './Forms/ProgressBar';
import TextInput from './Forms/TextInput';
import SelectInput from './Forms/SelectInput';
import { GlobalVariables } from '../../../GlobalVariables';
import VerticalStretch from './Forms/VerticalStretch';
import { useSelector } from 'react-redux';
import { receiptIcon } from '../Expenses';
import { documentLoadingErrorState, formUpdatingState } from '../../../redux/slices/statusesSlice';
import { ReduxUpdater } from '../../../redux/ReduxUpdater';
import { NavSpinner } from './Forms/NavSpinner';
import NavButtonsGroup from './Forms/NavButtonsGroup';
import Tooltip from '../tooltiped/Tooltip';
import { GlobalConstants } from '../../../GlobalConstants';
import ConfirmAndNextAdminMenu from './AdminMenu';
import TextAreaInput from './Forms/TextAreaInput';
import { FilterCategories } from '../../../Shared';
import { allowConfirm, checkReceiptData, requiresManagementAttention } from '../Helpers';

export interface IConfirmAndNextLockedFormProps {
    idx: number;
    max: number;
    receipt: IReceiptData;
    receiptChanges: IReceiptData;
    onConfirm: () => void;
    onSave: () => void;
    onPrev: () => void;
    onNext: () => void;
}

const ConfirmAndNextLockedForm = (props: IConfirmAndNextLockedFormProps) => {
    const currentImageLoadingErrorState = useSelector(documentLoadingErrorState);
    const isFormUpdating = useSelector(formUpdatingState);
    
    const { idx, max, receipt, receiptChanges, onConfirm, onPrev, onNext, onSave } = props;

    const [doAllow, setDoAllow] = React.useState(true);
    const [doFilter, setDoFilter] = React.useState(true);

    console.log('ConfirmAndNextLockedForm:', doAllow, doFilter, receipt, receiptChanges);

    const handleOnNext = () => {
        setDoAllow(true);
        setDoFilter(true);
        onNext();
    }

    const handleOnPrev = () => {
        setDoAllow(true);
        setDoFilter(true);
        onPrev();
    }

    const receiptStatus = React.useMemo(() => {
        if (receipt && Object.keys(receipt).length === 0) {
            return '???';
        }
        if (receipt.matched_bank_transaction) {
            return `Matched to${[null, '0.00'].includes(receipt.tip_amount) && (receipt.matched_bank_transaction.extracted_currency ?? 'EUR' === receipt.currency) ? ' transaction' : `: ${GlobalVariables.currencySymbol(receipt.matched_bank_transaction.extracted_currency)}${Math.abs(parseFloat(receipt.matched_bank_transaction.extracted_amount)).toFixed(2)}`}`;
            // return `Matched to ${receipt.matched_bank_transaction.extracted_type === 'ECOM' ? 'ecom' : 'card'} transaction`;
        }
        return requiresManagementAttention(receipt) ? 'Requires MGM attention' : 'Boat Cash';
    }, [receipt]);

    const date = receiptChanges.transaction_date ?? receipt.transaction_date;
    const TS = Date.now();
    const ID = `${receipt.id}-${TS}`;

    const mergedData = {
        ...receipt,
        ...receiptChanges,
    };

    const handleConfirm = () => {
        if (checkReceiptData(mergedData, GlobalVariables.coOwnersExtendedArray, currentImageLoadingErrorState)) {
            onConfirm();
        }
    }

    const handleSave = () => {
        setTimeout(() => {
            onSave();
        }, 250);
    }

    const handleRevert = () => {
        ReduxUpdater.receiptChanges = {} as IReceiptData;
    }

    GlobalVariables.allowReceiptChanges = false;
    React.useEffect(() => {
        // console.log('Reset values');
        // ReduxUpdater.receiptChanges = {} as IReceiptData;
        setTimeout(() => {
            GlobalVariables.allowReceiptChanges = true;
        }, 250);
    });

    const showAll = receipt.user_description && receipt.user_description.toLowerCase().indexOf('food') !== -1;
    const filteredCategories = React.useMemo(() => {
        const filtered = FilterCategories(receipt, doFilter);

        if (filtered.length > 2) {
            setDoAllow(false);
        }

        return filtered;
    }, [doFilter, receipt]);

    if (receipt && Object.keys(receipt).length === 0) {
        return <></>;
    }

    var diff_amount = '';
    const receiptCurrency = GlobalVariables.currencySymbol(receipt.currency ?? 'EUR');
    if (![null, '', '0.00'].includes(receipt.tip_amount) && receipt.amount !== null && receipt.amount !== '') {
        const tip = parseFloat(receipt.tip_amount);
        const tipPercent = tip * 100 / parseFloat(receipt.amount);
        const isTip = receipt.maris_category_id && GlobalVariables.accountingCategoriesByID[receipt.maris_category_id].includes('F&B') && tipPercent >= 5.0;
        diff_amount = isTip ? ` + ${tipPercent.toFixed(0)}% tip` : ` + ${receiptCurrency}${tip.toFixed(2)} diff.`;
    }

    return (
        <div id={`confirmAndNextLocked-${ID}`} className="h-[100%] w-full inline-flex flex-col justify-start items-start gap-4 relative">
            {ConfirmAndNextAdminMenu(receipt)}
            <DateTimeInput
                type={
                    receipt.matched_bank_transaction?.extracted_type !== 'ECOM' ?
                    'locked' :
                    (date === null || date === '' ? 'warning' : 'locked')
                }
                label="Date / Time"
                receipt={mergedData}
                onUpdate={(newDate) => {
                    if (GlobalVariables.allowReceiptChanges && mergedData.transaction_date !== newDate) {
                        console.log(`transaction_date CHANGES: |${mergedData.transaction_date}|${newDate}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            message_id: receipt.message_id,
                            transaction_date: newDate,
                            transaction_time: null,
                        };
                    }
                }}
            />
            <TextInput idx={`merchant-${ID}`} type="locked" label="Merchant" value={receipt.vendor_name ?? ''} />
            <TextInput idx={`amount-${ID}`} type="locked" label="Amount" value={`${receiptCurrency}${receipt.amount}${diff_amount}`} />
            <TextInput idx={`receiptStatus-${ID}`} type="locked" label="Receipt Status"
                value={receiptStatus}
                icon={
                    [GlobalConstants.UserRole.ROOT, GlobalConstants.UserRole.ADMIN].includes(ReduxUpdater.authenticatedUser?.user_role ?? '') ?
                        <Tooltip
                            className={`items-center justify-center clickable`}
                            text={receipt.matched_bank_transaction?.reference_text ?? ''}
                            placement='top-end'
                            textStyling='z-10 w-[500px] whitespace-break-spaces'
                        >
                            {receiptIcon(receipt)}
                        </Tooltip>
                        : receiptIcon(receipt)
                }
            />
            <TextAreaInput
                idx={`userDescription-${ID}`}
                type="edit"
                label="User Description"
                value={mergedData.user_description ?? ''}
                onChange={(newValue) => {
                    if (GlobalVariables.allowReceiptChanges && (receipt.user_description ?? '') !== newValue) {
                        console.log(`user_description CHANGES: |${receipt.user_description ?? ''}|${newValue}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            user_description: newValue,
                        }
                    }
                }}
            />
            <SelectInput
                idx={`category-${ID}`}
                type={mergedData.maris_category_id !== null ? 'edit' : 'empty'}
                label="Category"
                value={(mergedData.maris_category_id ?? -1).toString()}
                options={filteredCategories}
                selectClasses=''
                onChange={(newValue) => {
                    if (GlobalVariables.allowReceiptChanges && receipt.maris_category_id !== parseInt(newValue)) {
                        console.log(`maris_category_id CHANGES: |${receipt.maris_category_id}|${newValue}|`);
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            maris_category_id: parseInt(newValue),
                        }
                    }
                }}
                titleOrAction={showAll && doFilter && doAllow
                    ? 
                        <button
                            className="text-text-action-button"
                            onClick={() => setDoFilter(false)}
                        >
                            Show all
                        </button> 
                    : ''
                }
            />
            {GlobalVariables.coOwners.length > 1 && <SelectInput
                idx={`coOwner-${ID}`}
                type="edit"
                label="Co-owner Expense Allocation"
                value={mergedData.co_owner_name ?? ''}
                options={GlobalVariables.coOwnersExtendedArray}
                onChange={(newValue) => {
                    if (GlobalVariables.allowReceiptChanges && receipt.co_owner_name !== newValue) {
                        ReduxUpdater.receiptChanges = {
                            ...receiptChanges,
                            id: receipt.id,
                            co_owner_name: newValue,
                        }
                    }
                }}
            />}
            <VerticalStretch />
            <ProgressBar value={idx+1} max={max} />
            {
                isFormUpdating
                ? <NavSpinner label="Saving..." />
                : <NavButtonsGroup label={allowConfirm(receipt) ? 'Confirm & Next' : 'Next'} max={max} onRevert={handleRevert} onSave={handleSave} onPrev={handleOnPrev} onConfirm={allowConfirm(receipt) ? handleConfirm : onNext} onNext={handleOnNext} />
            }
        </div>
    );
};
 
export default ConfirmAndNextLockedForm;